import { CREATOR_USERNAME_COLUMN } from "@unbox/api";
import { XCircle } from "@untitledui/icons";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";

// Bolds the first occurrence of the creator username column in the message, so the
// common missing-column case reads like the reference design while other
// validation errors (empty file, over the row cap) still render without a false bold.
function renderMessage(message: string) {
  const idx = message.indexOf(CREATOR_USERNAME_COLUMN);
  if (idx === -1) return message;
  return (
    <>
      {message.slice(0, idx)}
      <strong className="font-semibold text-primary">{CREATOR_USERNAME_COLUMN}</strong>
      {message.slice(idx + CREATOR_USERNAME_COLUMN.length)}
    </>
  );
}

// A single example column: the creator username header followed by two sample rows.
function FormatColumn({ rows }: { rows: string[] }) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-secondary">
      <div className="border-b border-secondary bg-secondary px-3 py-2 text-xs font-medium text-tertiary">
        {CREATOR_USERNAME_COLUMN}
      </div>
      {rows.map((row) => (
        <div key={row} className="border-b border-secondary px-3 py-2 text-sm text-primary last:border-b-0">
          {row}
        </div>
      ))}
    </div>
  );
}

interface BlocklistImportErrorDrawerProps {
  message: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownloadTemplate: () => void;
}

export function BlocklistImportErrorDrawer({
  message,
  open,
  onOpenChange,
  onDownloadTemplate,
}: BlocklistImportErrorDrawerProps) {
  if (!open) return null;

  return (
    <SlideoutMenu isOpen onOpenChange={onOpenChange} isDismissable>
      <SlideoutMenu.Header onClose={() => onOpenChange(false)}>
        <div className="flex flex-row items-center gap-3">
          <FeaturedIcon icon={XCircle} color="error" theme="light" size="md" />
          <h2 className="text-lg font-semibold text-primary">Wrong file type</h2>
        </div>
      </SlideoutMenu.Header>

      <SlideoutMenu.Content className="gap-6">
        <p className="text-sm text-tertiary">{renderMessage(message)}</p>

        <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4">
          <p className="text-sm font-medium text-primary">Expected CSV format.</p>
          <div className="flex flex-row items-center gap-3">
            <FormatColumn rows={["@jane_smith", "@amanda_well"]} />
            <span className="text-xs font-medium text-tertiary">OR</span>
            <FormatColumn rows={["jane_smith", "amanda_well"]} />
          </div>
        </div>

        <p className="text-sm text-tertiary">
          <button type="button" className="underline text-primary" onClick={onDownloadTemplate}>
            Download the template
          </button>{" "}
          to match the format, then upload again.
        </p>
      </SlideoutMenu.Content>

      <SlideoutMenu.Footer className="flex flex-row justify-end">
        <Button color="primary" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      </SlideoutMenu.Footer>
    </SlideoutMenu>
  );
}
